import React from "react";
import { Bell, BellOff } from "lucide-react";
import HoverTip from "@/components/HoverTip";

/**
 * Three-state master bell shown in the header of the alerts column on the
 * Watchlist and Portfolio tables.
 *
 * States:
 *   - "on":            every row has alerts enabled.
 *   - "off":           no row has alerts enabled.
 *   - "indeterminate": some rows have alerts enabled.
 *
 * Visuals:
 *   - on  → solid green Bell.
 *   - off → muted BellOff.
 *   - indeterminate → amber Bell (no background: the tinted half looked like a shadow).
 *
 * Click behaviour:
 *   - on            → turn all off.
 *   - off / indet.  → turn all on (so an "indeterminate" click is a positive action).
 */
export default function MasterAlertToggle({ total, activeCount, onChange, disabled = false, testid = "alert-toggle-all", info = null }) {
    if (total <= 0) return null;
    const state = activeCount === 0 ? "off" : activeCount === total ? "on" : "indeterminate";
    const next = state === "on" ? false : true;

    const tip = state === "on"
        ? `Desactivar todas las alertas (${activeCount}/${total} activas)`
        : `Activar todas las alertas (${activeCount}/${total} activas)`;

    const baseColor =
        state === "on" ? "var(--cheap)"
        : state === "indeterminate" ? "#D97706"  /* amber */
        : "var(--text-primary)";

    const Icon = state === "off" ? BellOff : Bell;

    // `info`: how the email alerts work. On a computer it shows with the bell's own tip; a ⓘ
    // beside it gives it on a phone too (tapping the bell would switch every alert).
    const bell = (
        <HoverTip text={info ? `${tip}\n\n${info}` : tip} maxWidth={380}>
            <button
                type="button"
                onClick={() => onChange(next)}
                disabled={disabled}
                className="inline-flex items-center justify-center w-6 h-6 hover:opacity-70 transition-opacity disabled:opacity-40"
                style={{ color: baseColor }}
                aria-pressed={state === "on"}
                aria-label={tip}
                data-testid={testid}
                data-state={state}
            >
                <Icon size={14} strokeWidth={state === "on" ? 2.5 : 2} />
            </button>
        </HoverTip>
    );
    if (!info) return bell;
    return (
        <span className="inline-flex items-center gap-0.5">
            {bell}
            <HoverTip text={info} maxWidth={380}>
                <span className="cursor-help text-[#9CA3AF] text-xs normal-case" data-testid={`${testid}-info`}>ⓘ</span>
            </HoverTip>
        </span>
    );
}
